import { z } from 'zod';

// ---- Notification domain primitives (#1068) -----------------------------
//
// Foundational layer for the parallel notifications lane (epic #1046).
// This file owns the wire vocabulary used by:
//   - the backend persistence layer (`NotificationPreferences` /
//     `NotificationAggregate` Prisma models),
//   - the realtime invalidation event (`NOTIFICATIONS_UPDATED`),
//   - the web settings UI / bell panel / per-tab badges in later
//     parallel-lane issues (#1069, #1070, #1072, #1073).
//
// Routing/notification vocabularies live in shared so emitters and
// router/badge code agree on a single source of truth — adding a new
// notification kind requires touching only this file plus the consumer.

// ---------- 1. Fixed enums (stable routing domain) -----------------------

/**
 * @aiPurpose stable entity-type discriminator for the aggregate row's
 *   logical target. `LR-NNNNN` routes to the China request drawer;
 *   `RU-NNNNN` to the RF request drawer; `LEAD` carries a UUID `Lead.id`
 *   and routes to the CRM lead (#1359, `lead_callback`).
 * @aiInvariant the DB column is a Postgres ENUM. #1359 adds `LEAD` to
 *   this wire vocabulary as a contract only — no emitter writes a `LEAD`
 *   aggregate yet, so the Postgres enum stays unchanged until the
 *   emitter lands. Widening the persisted set later is a coordinated
 *   Prisma migration. Append-only.
 */
export const NotificationEntityTypeSchema = z.enum([
  'CHINA_REQUEST',
  'RUSSIA_REQUEST',
  'LEAD',
  'CARGO_PROBLEM',
]);
export type NotificationEntityType = z.infer<typeof NotificationEntityTypeSchema>;

/**
 * @aiPurpose stable routing tab key. Mirrors the visible drawer tab
 *   strip on both China and RF request drawers so a bell-panel click
 *   navigates to the right tab. `null`/omitted is allowed because
 *   responsible-status notifications open the request without
 *   selecting a tab.
 * @aiInvariant DB column is a Postgres ENUM. The web client MUST
 *   reject unknown values rather than route as a free-form string.
 */
export const NotificationRoutingTabSchema = z.enum([
  'info',
  'items',
  'boxes',
  'carriers',
  'chat',
  'audit',
]);
export type NotificationRoutingTab = z.infer<typeof NotificationRoutingTabSchema>;

// ---------- 2. Notification type vocabulary (varchar + Zod) --------------

/**
 * @aiPurpose closed validated set of notification kinds for v1.
 *   Stored in the DB as varchar with shared-Zod validation so a future
 *   kind can be added without a DB enum migration. The notification
 *   kind also determines which preference group's enabled/sound key
 *   applies via `notificationTypeToPreferenceGroup`.
 * @aiInvariant `.enum([...])` is closed; the backend service rejects
 *   unknown values at the boundary, so a stale client cannot spoof a
 *   new kind. Adding a new kind requires: (1) extend this enum, (2)
 *   add it to `notificationTypeToPreferenceGroup`, (3) update
 *   downstream consumers + tests.
 */
export const NotificationTypeSchema = z.enum([
  'chat',
  'rate',
  'rejection',
  'insurance',
  'responsibleStatus',
  'lead_callback',
  'cargo_problem',
]);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

/**
 * @aiPurpose preference grouping for v1. UI shows three controls:
 *   "Chat", "Rates/rejections/insurance", "Responsible-status".
 * @aiInvariant `.enum([...])` is closed; settings UI and backend share
 *   this exact set. A new group requires a coordinated Prisma + shared
 *   + UI change.
 */
export const NotificationPreferenceGroupSchema = z.enum(['chat', 'rates', 'responsibleStatus']);
export type NotificationPreferenceGroup = z.infer<typeof NotificationPreferenceGroupSchema>;

/**
 * @aiPurpose closed mapping from a notification kind to the
 *   preference group that gates it. Used by the backend emitter to
 *   skip suppressed groups and by the web sound player to pick the
 *   per-group sound key.
 * @aiInvariant the four "rates" group members (`rate`, `rejection`,
 *   `insurance`) collapse to one toggle. `chat` and `responsibleStatus`
 *   are 1:1.
 */
export const notificationTypeToPreferenceGroup: Readonly<
  Record<NotificationType, NotificationPreferenceGroup>
> = Object.freeze({
  chat: 'chat',
  rate: 'rates',
  rejection: 'rates',
  insurance: 'rates',
  responsibleStatus: 'responsibleStatus',
  // WHY (#1359): the lead-callback notification reuses the existing
  //   responsible-status preference group rather than introducing a new
  //   toggle — it is a manager-facing hand-off cue like the others.
  lead_callback: 'responsibleStatus',
  cargo_problem: 'responsibleStatus',
});

// ---------- 3. Sound catalog (fixed allowlist) ---------------------------

/**
 * @aiPurpose closed allowlist of audio file keys served from
 *   `apps/web/public/audio/notifications/`. Frontend select UI binds
 *   to this set; backend preferences write path rejects anything
 *   outside it so a stale client cannot persist an arbitrary string.
 * @aiInvariant adding a new sound requires a coordinated change:
 *   place the mp3 under the web public dir AND extend this enum.
 */
export const NotificationSoundKeySchema = z.enum([
  '47313572-ui-pop-sound-316482.mp3',
  'arnav_geddada-ui-sound-374228.mp3',
  'creatorshome-sharp-clap-329723.mp3',
  'freesound_community-button-pressed-38129.mp3',
  'juniorsoundays-ui-sound-108-532177.mp3',
  'juniorsoundays-ui-sound-26-527809.mp3',
  'ksjsbwuil-apple-pay-success-sound-effect-481188.mp3',
]);
export type NotificationSoundKey = z.infer<typeof NotificationSoundKeySchema>;

/**
 * @aiPurpose ordered readonly array of all valid sound keys, useful for
 *   building the settings select UI (#1069). The order is the canonical
 *   display order; the default (`NOTIFICATION_DEFAULT_SOUND_KEY`) is
 *   the first entry.
 */
export const NOTIFICATION_SOUND_KEYS: ReadonlyArray<NotificationSoundKey> =
  NotificationSoundKeySchema.options;

/**
 * @aiPurpose legacy alias for the chat-group default sound key (#1155).
 *   Until #1155 this constant fed all three preference groups; the
 *   defaults are now per-group. New consumers MUST pick the appropriate
 *   `NOTIFICATION_DEFAULT_SOUND_KEY_<GROUP>` constant or the
 *   `NOTIFICATION_DEFAULT_SOUND_KEY_BY_GROUP` map below. This export is
 *   kept to preserve the historical invariant
 *   `NOTIFICATION_SOUND_KEYS[0] === NOTIFICATION_DEFAULT_SOUND_KEY`
 *   asserted by existing tests.
 */
export const NOTIFICATION_DEFAULT_SOUND_KEY: NotificationSoundKey =
  '47313572-ui-pop-sound-316482.mp3';

/**
 * @aiPurpose chat-group default sound key (#1155). Equals
 *   `NOTIFICATION_DEFAULT_SOUND_KEY`; kept as a dedicated export so
 *   consumers reading per-group state speak one vocabulary.
 */
export const NOTIFICATION_DEFAULT_SOUND_KEY_CHAT: NotificationSoundKey =
  '47313572-ui-pop-sound-316482.mp3';

/**
 * @aiPurpose rates-group (rates / rejections / insurance) default sound
 *   key (#1155). Mirrored in the Prisma column default for
 *   `ratesSoundKey` and applied to existing rows via the
 *   `<T+1>_backfill_notification_sound_defaults` migration.
 */
export const NOTIFICATION_DEFAULT_SOUND_KEY_RATES: NotificationSoundKey =
  'ksjsbwuil-apple-pay-success-sound-effect-481188.mp3';

/**
 * @aiPurpose responsibleStatus-group default sound key (#1155). Mirrored
 *   in the Prisma column default for `responsibleStatusSoundKey` and
 *   applied to existing rows via the backfill migration.
 */
export const NOTIFICATION_DEFAULT_SOUND_KEY_RESPONSIBLE_STATUS: NotificationSoundKey =
  'juniorsoundays-ui-sound-26-527809.mp3';

/**
 * @aiPurpose per-group default sound key map (#1155). Convenience
 *   for code paths that need to look up the default by a
 *   `NotificationPreferenceGroup` value rather than naming each
 *   constant.
 * @aiInvariant the three entries match
 *   `NOTIFICATION_DEFAULT_SOUND_KEY_<GROUP>` 1:1 and the values of
 *   `DEFAULT_NOTIFICATION_PREFERENCES.<group>.soundKey`.
 */
export const NOTIFICATION_DEFAULT_SOUND_KEY_BY_GROUP: Readonly<
  Record<NotificationPreferenceGroup, NotificationSoundKey>
> = Object.freeze({
  chat: NOTIFICATION_DEFAULT_SOUND_KEY_CHAT,
  rates: NOTIFICATION_DEFAULT_SOUND_KEY_RATES,
  responsibleStatus: NOTIFICATION_DEFAULT_SOUND_KEY_RESPONSIBLE_STATUS,
});

// ---------- 4. Entity-id grammar -----------------------------------------

const ChinaRequestIdSchema = z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN');
const RussiaRequestIdSchema = z.string().regex(/^RU-\d{5}$/, 'must match RU-NNNNN');
const LeadEntityIdSchema = z.string().uuid('must be a Lead UUID');
const CargoProblemEntityIdSchema = z.string().uuid('must be a Cargo Problem UUID');

/**
 * @aiPurpose discriminated entityId grammar — China rows use
 *   `LR-NNNNN`, RF rows use `RU-NNNNN`, and CRM lead rows (#1359) use a
 *   `Lead.id` UUID. Centralised so the bell UI, row badges, and realtime
 *   payload all speak the same grammar.
 */
export const NotificationEntityIdSchema = z.union([
  ChinaRequestIdSchema,
  RussiaRequestIdSchema,
  LeadEntityIdSchema,
  CargoProblemEntityIdSchema,
]);
export type NotificationEntityId = z.infer<typeof NotificationEntityIdSchema>;

/**
 * @aiPurpose superRefine helper that correlates `entityType` with
 *   `entityId` so a row carrying `(CHINA_REQUEST, RU-00001)`,
 *   `(RUSSIA_REQUEST, LR-00001)`, or `(LEAD, LR-00001)` rejects at the
 *   boundary. The primitive id regexes already exist; this helper is the
 *   cross-field check that makes the pair internally consistent. Reused
 *   on the aggregate wire row, mark-viewed-by-target body, realtime
 *   payload, and the backend emit() input schema so the contract speaks
 *   one target grammar end-to-end.
 * @aiInvariant exactly one id shape per entityType — `LEAD` requires a
 *   UUID and rejects the `LR-`/`RU-` codes, while `CHINA_REQUEST`/
 *   `RUSSIA_REQUEST` reject a UUID. Cross-format pairs always fail.
 */
export function refineNotificationTarget(
  val: { entityType: NotificationEntityType; entityId: string },
  ctx: z.RefinementCtx,
): void {
  if (val.entityType === 'CHINA_REQUEST' && !/^LR-\d{5}$/.test(val.entityId)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['entityId'],
      message: 'CHINA_REQUEST requires an LR-NNNNN entityId',
    });
  }
  if (val.entityType === 'RUSSIA_REQUEST' && !/^RU-\d{5}$/.test(val.entityId)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['entityId'],
      message: 'RUSSIA_REQUEST requires an RU-NNNNN entityId',
    });
  }
  if (val.entityType === 'LEAD' && !LeadEntityIdSchema.safeParse(val.entityId).success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['entityId'],
      message: 'LEAD requires a UUID Lead.id entityId',
    });
  }
  if (
    val.entityType === 'CARGO_PROBLEM' &&
    !CargoProblemEntityIdSchema.safeParse(val.entityId).success
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['entityId'],
      message: 'CARGO_PROBLEM requires a UUID CargoProblem.id entityId',
    });
  }
}

// ---------- 5. Aggregate wire row ----------------------------------------

/**
 * @aiPurpose one grouped notification aggregate as seen on the wire.
 *   The frontend bell panel renders this directly; row badges and
 *   per-tab badges select by `(entityType, entityId, notificationType)`.
 * @aiInvariant `unreadCount` is `0` for viewed aggregates; the wire
 *   still returns viewed rows so the bell history surface can render
 *   the most recent 30 days. `firstUnreadAt` is `null` when no unread
 *   events exist (either pristine viewed-marker or fully cleared).
 * @aiPii does NOT include actor names, request titles, chat text,
 *   commercial values, or addresses. The realtime invalidation event
 *   carries even less.
 */
export const NotificationAggregateItemSchema = z
  .object({
    id: z.string().uuid(),
    entityType: NotificationEntityTypeSchema,
    entityId: NotificationEntityIdSchema,
    notificationType: NotificationTypeSchema,
    routingTab: NotificationRoutingTabSchema.nullable(),
    unreadCount: z.number().int().nonnegative(),
    firstUnreadAt: z.string().datetime().nullable(),
    lastEventAt: z.string().datetime(),
    viewedAt: z.string().datetime().nullable(),
  })
  .strict()
  .superRefine(refineNotificationTarget);
export type NotificationAggregateItem = z.infer<typeof NotificationAggregateItemSchema>;

export const NotificationListResponseSchema = z.array(NotificationAggregateItemSchema);
export type NotificationListResponse = z.infer<typeof NotificationListResponseSchema>;

/**
 * @aiPurpose recent-history window. The backend list endpoint returns
 *   unread aggregates plus viewed aggregates whose `lastEventAt` falls
 *   inside this window. Server-side cleanup is a follow-up; this is
 *   purely a read-time filter for v1.
 */
export const NOTIFICATION_HISTORY_WINDOW_DAYS = 30;

// ---------- 6. Preferences (per-user) -----------------------------------

/**
 * @aiPurpose per-group preference block (enabled + soundKey). Used as
 *   a building block for both the wire row and the partial PATCH input.
 */
export const NotificationGroupPreferenceSchema = z
  .object({
    enabled: z.boolean(),
    soundKey: NotificationSoundKeySchema,
  })
  .strict();
export type NotificationGroupPreference = z.infer<typeof NotificationGroupPreferenceSchema>;

/**
 * @aiPurpose full preferences wire row. Per-user state served by
 *   `GET /api/notifications/preferences`. Defaults are applied at the
 *   service when the actor has no row yet, so the wire shape is always
 *   non-nullable.
 * @aiInvariant the three group keys are always present; the settings
 *   UI may render them in any order.
 */
export const NotificationPreferencesSchema = z
  .object({
    chat: NotificationGroupPreferenceSchema,
    rates: NotificationGroupPreferenceSchema,
    responsibleStatus: NotificationGroupPreferenceSchema,
  })
  .strict();
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

/**
 * @aiPurpose partial PATCH input. Any subset of the three groups may
 *   appear; within a group, either `enabled` or `soundKey` may be
 *   omitted (server preserves prior value).
 * @aiInvariant body MUST contain at least one writable field — empty
 *   `{}` rejects with `VALIDATION_ERROR` to avoid no-op writes.
 */
const PartialGroupSchema = z
  .object({
    enabled: z.boolean().optional(),
    soundKey: NotificationSoundKeySchema.optional(),
  })
  .strict()
  .refine(
    (v) => v.enabled !== undefined || v.soundKey !== undefined,
    'must set at least one of enabled, soundKey',
  );

export const NotificationPreferencesUpdateInputSchema = z
  .object({
    chat: PartialGroupSchema.optional(),
    rates: PartialGroupSchema.optional(),
    responsibleStatus: PartialGroupSchema.optional(),
  })
  .strict()
  .refine(
    (v) => v.chat !== undefined || v.rates !== undefined || v.responsibleStatus !== undefined,
    'must set at least one preference group',
  );
export type NotificationPreferencesUpdateInput = z.infer<
  typeof NotificationPreferencesUpdateInputSchema
>;

/**
 * @aiPurpose default preferences row returned when no DB row exists
 *   for the actor. Mirrors the Prisma column defaults exactly so the
 *   first read after sign-up looks identical to the post-write state.
 */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = Object.freeze({
  chat: { enabled: true, soundKey: NOTIFICATION_DEFAULT_SOUND_KEY_CHAT },
  rates: { enabled: true, soundKey: NOTIFICATION_DEFAULT_SOUND_KEY_RATES },
  responsibleStatus: { enabled: true, soundKey: NOTIFICATION_DEFAULT_SOUND_KEY_RESPONSIBLE_STATUS },
}) as NotificationPreferences;

// ---------- 7. Mark-viewed bodies (discriminated) -----------------------

const MarkViewedByIdSchema = z
  .object({
    by: z.literal('id'),
    id: z.string().uuid(),
  })
  .strict();

// WHY (#1380): the by-target path now accepts LEAD in addition to the
//   request domains. The LEAD emitter (`emitLeadCallback`, #1362) exists
//   and the #1363 lead-drawer auto-views on mount using this path. LEAD
//   still requires a UUID entityId (enforced by refineNotificationTarget)
//   and lead_callback is bound to LEAD only (see INVARIANT below).
const MarkViewedByTargetEntityTypeSchema = z.enum([
  'CHINA_REQUEST',
  'RUSSIA_REQUEST',
  'LEAD',
  'CARGO_PROBLEM',
]);
const MarkViewedByTargetEntityIdSchema = z.union([
  ChinaRequestIdSchema,
  RussiaRequestIdSchema,
  LeadEntityIdSchema,
  CargoProblemEntityIdSchema,
]);

// WHY (#1380): lead_callback is now accepted by the by-target path for
//   the #1363 lead-drawer auto-view. It is still bound exclusively to the
//   LEAD entityType — the superRefine below rejects any pairing with a
//   request entityType, preserving the #1359 R2-F-001 guard.
const MarkViewedByTargetNotificationTypeSchema = z.enum([
  'chat',
  'rate',
  'rejection',
  'insurance',
  'responsibleStatus',
  'lead_callback',
  'cargo_problem',
]);

const MarkViewedByTargetSchema = z
  .object({
    by: z.literal('target'),
    entityType: MarkViewedByTargetEntityTypeSchema,
    entityId: MarkViewedByTargetEntityIdSchema,
    notificationType: MarkViewedByTargetNotificationTypeSchema,
  })
  .strict();

/**
 * @aiPurpose body for `POST /api/notifications/mark-viewed`. The
 *   discriminator `by: 'id' | 'target'` distinguishes the bell-row
 *   click (id known) from the drawer/tab-mount auto-view (only the
 *   logical target known, even before the aggregate exists).
 * @aiInvariant the by-target branch is race-safe: the backend upserts
 *   a viewed marker so a concurrent emit cannot leave unread state.
 */
export const NotificationMarkViewedBodySchema = z
  .discriminatedUnion('by', [MarkViewedByIdSchema, MarkViewedByTargetSchema])
  .superRefine((val, ctx) => {
    // INVARIANT (#1068, Codex F-005): the by-target branch must carry a
    //   correlated (entityType, entityId). The by-id branch has only a
    //   uuid so it is left untouched. Keeping the cross-field check on
    //   the discriminatedUnion's superRefine preserves Zod's nice
    //   discriminator error messages on the outer schema.
    if (val.by === 'target') {
      refineNotificationTarget(val, ctx);
      // INVARIANT (#1380): lead_callback is a LEAD-only notificationType. The
      //   by-target enum now accepts it (LEAD drawer auto-view, #1363), but it
      //   must not pair with a request entityType — preserving the #1359
      //   R2-F-001 guard against stray cross-domain aggregate rows.
      if (val.notificationType === 'lead_callback' && val.entityType !== 'LEAD') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['notificationType'],
          message: 'lead_callback is only valid for the LEAD entityType',
        });
      }
      if (val.notificationType === 'cargo_problem' && val.entityType !== 'CARGO_PROBLEM') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['notificationType'],
          message: 'cargo_problem is only valid for the CARGO_PROBLEM entityType',
        });
      }
    }
  });
export type NotificationMarkViewedBody = z.infer<typeof NotificationMarkViewedBodySchema>;
